import { useState, useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import Rollbar from 'utils/rollbar'
import { isAuth0Timeout, isRoleAllowed, saveCustomerIdFromCustomerAuth } from 'utils/auth'
import {
  fetchCurrentUser,
  saveAuthToken,
  terminateSession,
  revokeFactSetIdpSession,
} from 'services/auth'
import { getValidOidcUser, getUserManager } from 'auth/factsetOidc'
import { updateUnleashUserId } from 'services/unleashClient'
import { factset as factsetConfig } from 'config'
import { setOidcTokenConfirmed } from 'config/apiBaseUrl'
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'

import { ForbiddenError } from 'constants/errors'
import { AUTH_ERROR_TYPES } from 'constants/errorCodes'
import { CUSTOMER_ID } from 'constants/localStorage'
import { FEATURE_FLAG_FACTSET_KONG_LIMA } from 'constants/featureFlag'
import {
  JUST_LOGGED_OUT_KEY,
  JUST_SESSION_EXPIRED_KEY,
  NEEDS_FACTSET_LOGIN_KEY,
  OIDC_CALLBACK_ERROR_KEY,
} from 'constants/sessionStorage'

const TOKEN_ERRORS = [
  'login_required',
  'consent_required',
  'account_selection_required',
  'interaction_required',
]

// Reads a sessionStorage flag exactly once, clears it if present.
// Used as a lazy useState initializer so each flag is consumed on mount and frozen
// for the hook's lifetime — re-renders cannot flip it back.
function readAndClearSessionFlag(key) {
  const wasSet = Boolean(sessionStorage.getItem(key))
  if (wasSet) {
    sessionStorage.removeItem(key)
  }
  return wasSet
}

// This token is only set up to minimize the impact of having an account
// that has not been properly configured and has a valid authenticated_id present,
// in this case the client cannot determine that the user is invalid (since it receives a 401)
// and will attempt to reauthenticate against the tenant. Since the session is valid
// on auth0 the flow will continue to the API, where the invalid user will once again
// fail to be authenticated. To avoid this infinite redirect loop, we set a control flag and report
// that a problem was encountered.
const AUTH0_AUTH_ATTEMPT_TOKEN = 'auth0-authentication-attempted'
export const auth0AuthAttempted = () => !!localStorage.getItem(AUTH0_AUTH_ATTEMPT_TOKEN)
export const registerAuth0AuthAttempt = () => localStorage.setItem(AUTH0_AUTH_ATTEMPT_TOKEN, true)

function useAuth() {
  const [currentUser, setCurrentUser] = useState(null)

  // These must be lazy useState initializers, NOT values recomputed on each render. Each one
  // runs its cleanup exactly once on mount and freezes the flag for the hook's lifetime. If we
  // recomputed inline, a re-render would read the flag as gone, flip it to false, and let
  // fetchAuth0User silently re-auth the user against PingFederate's persistent session —
  // exactly what these guards prevent.
  const [isSignedOut] = useState(() => readAndClearSessionFlag(JUST_LOGGED_OUT_KEY))
  const [isSessionExpired] = useState(() => readAndClearSessionFlag(JUST_SESSION_EXPIRED_KEY))
  const [needsFactsetLogin] = useState(() => readAndClearSessionFlag(NEEDS_FACTSET_LOGIN_KEY))
  const [oidcCallbackFailed] = useState(() => readAndClearSessionFlag(OIDC_CALLBACK_ERROR_KEY))

  const { isLoading, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0()
  const sessionInitializedRef = useRef(false)
  const authPathRef = useRef(null)

  const isKongLimaEnabled = useFlag(FEATURE_FLAG_FACTSET_KONG_LIMA)
  const { flagsReady, flagsError } = useFlagsStatus()

  useEffect(() => {
    if (isSignedOut || isSessionExpired || needsFactsetLogin || oidcCallbackFailed) {
      return
    }

    // Wait for flags to resolve before choosing the auth path.
    // Without this, a fresh session (no Unleash cache) takes the Auth0 path while the flag
    // is still loading, then baseUrl flips to Kong once flags arrive — causing a race.
    // If the flag proxy errored, fall through immediately — useFlag returns false on failure,
    // so the Auth0 path is taken as a safe default.
    if (factsetConfig.oidcAuthority && factsetConfig.oidcClientId && !flagsReady && !flagsError) {
      return
    }

    // Skip if already initialized — unless the Auth0 path ran on a stale flag value
    // and the flag has since flipped to true (cold Unleash cache on review apps).
    if (sessionInitializedRef.current) {
      if (authPathRef.current === 'auth0' && isKongLimaEnabled) {
        sessionInitializedRef.current = false
      } else {
        return
      }
    }

    async function initSession() {
      try {
        if (auth0AuthAttempted()) {
          return
        }

        if (isKongLimaEnabled && factsetConfig.oidcAuthority && factsetConfig.oidcClientId) {
          const storedUser = await getValidOidcUser()
          if (!storedUser) {
            return
          }
          saveAuthToken(storedUser.access_token)
          setOidcTokenConfirmed(true)
          await fetchUser()
          authPathRef.current = 'oidc'
          sessionInitializedRef.current = true
          return
        }

        const token = await getAccessTokenSilently()
        saveAuthToken(token)
        await fetchUser()
        authPathRef.current = 'auth0'
        sessionInitializedRef.current = true
      } catch (error) {
        if (TOKEN_ERRORS.includes(error?.error)) {
          sessionStorage.setItem(NEEDS_FACTSET_LOGIN_KEY, 'true')
          window.location.href = window.location.origin
          return
        }

        // ignore timeout errors to avoid excesive noise if the refresh fails,
        // in most cases this is a false positive due to the auth token being valid
        // for a day
        if (isAuth0Timeout(error)) {
          return
        }

        setCurrentUser(() => {
          throw error
        })
      }
    }

    async function fetchUser() {
      try {
        localStorage.removeItem(CUSTOMER_ID)
        const user = await fetchCurrentUser()

        if (!isRoleAllowed(user)) {
          throw new ForbiddenError({ user })
        }

        saveCustomerIdFromCustomerAuth(user.customerAuthorizations)
        Rollbar.configure({ payload: { person: { id: user.id, email: user.email } } })
        updateUnleashUserId(user.id)

        setCurrentUser(user)
      } catch (error) {
        if (
          error?.response?.data?.errors?.some(
            e =>
              e.code === AUTH_ERROR_TYPES.identityLocked ||
              e.code === AUTH_ERROR_TYPES.notConfigured,
          )
        ) {
          throw error
        }

        if (error?.response?.status === 401) {
          registerAuth0AuthAttempt()
        }

        throw error
      }
    }

    initSession()
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    isSignedOut,
    isSessionExpired,
    needsFactsetLogin,
    oidcCallbackFailed,
    isKongLimaEnabled,
    flagsReady,
    flagsError,
  ])

  async function endSession() {
    try {
      await terminateSession()
    } catch {
      // ignore failures terminating the session, this is done optimistically
      // to invalidate the token on our end and issue a new one during the next login
    } finally {
      setOidcTokenConfirmed(false)
      sessionStorage.setItem(JUST_LOGGED_OUT_KEY, 'true')

      try {
        await getUserManager().removeUser()
      } catch {
        // ignore — OIDC session may not exist
      }

      try {
        await revokeFactSetIdpSession(factsetConfig.authLogoutUrl)
      } catch {
        // ignore — proceed with Auth0 logout regardless
      }

      logout({ returnTo: window.location.origin })
    }
  }

  function signIn() {
    loginWithRedirect({
      appState: { returnTo: '/' },
      redirectUri: window.location.origin,
      connection: 'factset',
    })
  }

  async function refetchCurrentUser() {
    const user = await fetchCurrentUser()
    setCurrentUser(user)
  }

  return {
    state: {
      isLoading,
      currentUser,
      isSignedOut,
      isSessionExpired,
      needsFactsetLogin,
      oidcCallbackFailed,
    },
    handlers: { endSession, refetchCurrentUser, signIn },
  }
}

export default useAuth
